import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Upload, Trash2, Loader2, FileDown, Music } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ScorePublish } from "@/components/ScorePublish";

const KEYS = ["C", "C#", "Db", "D", "Eb", "E", "F", "F#", "Gb", "G", "Ab", "A", "Bb", "B", "Cb"];
const PRESETS = [["concert", "Concert pitch (piano, voice, flute, violin)"], ["bb", "B♭ instrument (trumpet, clarinet, soprano sax)"], ["tenorsax", "B♭ tenor sax"],
  ["eb", "E♭ instrument (alto sax, baritone sax)"], ["horn", "F horn"], ["guitar", "Guitar (treble clef, sounds an octave lower)"], ["bassguitar", "Bass guitar (bass clef, sounds an octave lower)"]];
const LAYOUTS = [["original", "Original layout"], ["piano", "Piano — treble & bass clef (grand staff)"], ["singer", "Singer — one vocal part"], ["leadsheet", "Lead sheet — melody, chords & lyrics"]];
const VOICES = [["soprano", "Soprano (treble clef)"], ["alto", "Alto (treble clef)"], ["tenor", "Tenor (treble clef, 8 below)"], ["bass", "Bass (bass clef)"]];
const pretty = (k) => k?.replace(/b(?= |$)/, "♭").replace("#", "♯");

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1 text-sm"><span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">{label}</span>{children}</label>
);
const sel = "rounded-xl border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none";

const UploadBox = ({ onUpload, busy }) => {
  const [drag, setDrag] = useState(false);
  const drop = (e) => { e.preventDefault(); setDrag(false); if (!busy) onUpload(e.dataTransfer.files[0]); };
  return (
    <label onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={drop}
      className={`flex flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed p-5 text-center text-sm transition-colors ${busy ? "cursor-wait border-primary/30 opacity-70" : "cursor-pointer border-primary/50 hover:bg-primary/5"} ${drag ? "bg-primary/10" : ""}`} data-testid="score-upload">
      <span className="inline-flex items-center gap-2 font-semibold text-primary">{busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {busy ? `Uploading ${busy}…` : "Upload MusicXML"}</span>
      {!busy && <span className="text-xs text-muted-foreground">Click or drop a .musicxml, .xml or .mxl file</span>}
      <input type="file" accept=".musicxml,.xml,.mxl" className="hidden" disabled={!!busy} onChange={(e) => { onUpload(e.target.files[0]); e.target.value = ""; }} data-testid="score-upload-input" />
    </label>
  );
};

const Library = ({ scores, active, onPick, onDelete, onUpload, busy }) => (
  <aside className="space-y-3" data-testid="score-library">
    <UploadBox onUpload={onUpload} busy={busy} />
    {scores.length === 0 && <p className="text-sm text-muted-foreground" data-testid="score-library-empty">No scores yet. In Sibelius: File → Export → MusicXML.</p>}
    {scores.map((s) => (
      <div key={s.id} className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2 ${active?.id === s.id ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`score-item-${s.id}`}>
        <button type="button" onClick={() => onPick(s)} className="min-w-0 flex-1 text-left" data-testid={`score-pick-${s.id}`}>
          <p className="truncate font-medium">{s.title}</p>
          <p className="text-xs text-muted-foreground">{pretty(s.key)} · {s.parts.length} part{s.parts.length > 1 ? "s" : ""}</p>
        </button>
        <button type="button" onClick={() => onDelete(s)} title="Delete" data-testid={`score-delete-${s.id}`}><Trash2 size={15} className="text-muted-foreground hover:text-destructive" /></button>
      </div>
    ))}
  </aside>
);

const download = async (id, fmt, body) => {
  const res = await api.post(`/admin/scores/${id}/export/${fmt}`, body, { responseType: "blob" });
  const name = (res.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || `score.${fmt}`;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

export default function AdminScores() {
  const { user } = useAuth();
  const [scores, setScores] = useState([]);
  const [active, setActive] = useState(null);
  const [opts, setOpts] = useState({ target: "", auto_spell: true, preset: "concert", layout: "original", part_index: 0, voice: "soprano" });
  const [view, setView] = useState(null);
  const [busy, setBusy] = useState("");
  const [uploading, setUploading] = useState("");
  const load = () => api.get("/admin/scores").then(({ data }) => setScores(data.scores)).catch(() => {});
  useEffect(() => { load(); }, []);
  const body = { ...opts, target: opts.target || null };
  useEffect(() => {
    if (!active) return undefined;
    let live = true;
    setBusy("render");
    api.post(`/admin/scores/${active.id}/render`, { ...opts, target: opts.target || null })
      .then(({ data }) => live && setView(data)).catch((e) => { if (live) { setView(null); toast.error(e.response?.data?.detail || "Could not render"); } })
      .finally(() => live && setBusy(""));
    return () => { live = false; };
  }, [active, opts]);
  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    setUploading(file.name);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/scores", fd); toast.success(`${data.title} added (${pretty(data.key)})`); await load(); setActive(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(""); setUploading("");
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}" from the library? Any copies sent to lessons are removed too.`)) return;
    await api.delete(`/admin/scores/${s.id}`); if (active?.id === s.id) { setActive(null); setView(null); } load();
  };
  const set = (k) => (e) => setOpts((o) => ({ ...o, [k]: k === "part_index" ? Number(e.target.value) : k === "auto_spell" ? e.target.checked : e.target.value }));
  const exportAs = async (fmt) => { setBusy(fmt); try { await download(active.id, fmt, body); } catch { toast.error("Export failed"); } setBusy(""); };
  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-8" data-testid="admin-scores-page">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary" data-testid="admin-scores-back"><ArrowLeft size={14} /> Admin</Link>
        <h1 className="mt-4 font-serif text-4xl tracking-tight sm:text-5xl">Transpose a score</h1>
        <p className="mt-3 max-w-2xl text-base text-muted-foreground">Upload a MusicXML file from Sibelius, choose a key, instrument and layout, then download a print-ready PDF or MusicXML to reopen in Sibelius.</p>
        <div className="mt-8 grid gap-8 lg:grid-cols-[260px_1fr]">
          <Library scores={scores} active={active} onPick={(s) => { setActive(s); setOpts((o) => ({ ...o, part_index: 0 })); }} onDelete={remove} onUpload={upload} busy={uploading} />
          {!active ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center rounded-3xl border border-border text-muted-foreground" data-testid="score-none"><Music size={32} /><p className="mt-2">Pick or upload a score</p></div>
          ) : (
            <section data-testid="score-workspace">
              <div className="grid gap-3 rounded-2xl border border-border bg-card p-4 sm:grid-cols-2">
                <Field label={`Key (original ${pretty(active.key)})`}>
                  <select value={opts.target} onChange={set("target")} className={sel} data-testid="score-key">
                    <option value="">Keep original key</option>
                    {KEYS.map((k) => <option key={k} value={k}>{pretty(k)} {active.key.split(" ")[1]}</option>)}
                  </select>
                </Field>
                <Field label="Instrument">
                  <select value={opts.preset} onChange={set("preset")} className={sel} data-testid="score-preset">{PRESETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                </Field>
                <Field label="Layout">
                  <select value={opts.layout} onChange={set("layout")} className={sel} data-testid="score-layout">{LAYOUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                </Field>
                {(opts.layout === "singer" || opts.layout === "leadsheet" || (opts.layout === "piano" && active.parts.length === 1)) && (
                  <Field label={opts.layout === "singer" ? "Vocal part" : "Melody part"}>
                    <select value={opts.part_index} onChange={set("part_index")} className={sel} data-testid="score-part">{active.parts.map((p, i) => <option key={p + i} value={i}>{p}</option>)}</select>
                  </Field>
                )}
                {opts.layout === "singer" && (
                  <Field label="Voice type (clef)">
                    <select value={opts.voice} onChange={set("voice")} className={sel} data-testid="score-voice">{VOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  </Field>
                )}
                <label className="flex items-center gap-2 self-end pb-2 text-sm" data-testid="score-autospell">
                  <input type="checkbox" checked={opts.auto_spell} onChange={set("auto_spell")} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid="score-autospell-input" />
                  Use the easier key spelling (e.g. F♯ instead of G♭)
                </label>
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                {view && <p className="text-sm" data-testid="score-result-key">Sounds in <strong>{pretty(view.concert_key)}</strong>{view.written_key !== view.concert_key && <> · written in <strong>{pretty(view.written_key)}</strong></>}</p>}
                <button type="button" onClick={() => exportAs("pdf")} disabled={!view || !!busy} data-testid="score-download-pdf" className="ml-auto inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50">
                  {busy === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download PDF
                </button>
                <button type="button" onClick={() => exportAs("musicxml")} disabled={!view || !!busy} data-testid="score-download-musicxml" className="inline-flex items-center gap-2 rounded-full border border-primary px-5 py-2 text-sm font-semibold text-primary disabled:opacity-50">
                  {busy === "musicxml" ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download MusicXML
                </button>
              </div>
              <div className="relative mt-4 space-y-4" data-testid="score-preview">
                {busy === "render" && <div className="absolute inset-0 z-10 flex items-start justify-center bg-background/60 pt-20"><Loader2 className="animate-spin text-primary" size={28} /></div>}
                {view?.pages.map((svg, i) => <div key={i} className="overflow-x-auto rounded-xl bg-white p-2 shadow" data-testid={`score-page-${i}`} dangerouslySetInnerHTML={{ __html: svg }} />)}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">Always give the PDF a quick check before printing, especially chord symbols and lyrics.</p>
              {view && <ScorePublish score={active} body={body} title={active.title} />}
            </section>
          )}
        </div>
      </main>
    </div>
  );
}
